import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { Button, Input, Alert } from '../../components/common';
import './AuthPages.css';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: 输入邮箱, 2: 输入验证码和新密码
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [formData, setFormData] = useState({
    email: '',
    code: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setMessage({ type: '', text: '' });
  };

  const handleRequestCode = async (e) => {
    e.preventDefault();

    if (!formData.email) {
      setMessage({ type: 'error', text: '请输入邮箱地址' });
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.requestPasswordReset(formData.email);
      setMessage({ type: 'success', text: response.data.message || '验证码已发送' });
      setStep(2);

      // 开发环境显示验证码
      if (response.data.debug_code) {
        setMessage({
          type: 'info',
          text: `验证码已发送。开发模式验证码：${response.data.debug_code}`,
        });
      }
    } catch (err) {
      const errorMsg = err.response?.data?.email?.[0] ||
        err.response?.data?.detail ||
        '发送验证码失败，请稍后重试';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!formData.code) {
      setMessage({ type: 'error', text: '请输入验证码' });
      return;
    }

    if (!formData.newPassword || !formData.confirmPassword) {
      setMessage({ type: 'error', text: '请输入新密码' });
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setMessage({ type: 'error', text: '两次输入的密码不一致' });
      return;
    }

    if (formData.newPassword.length < 8) {
      setMessage({ type: 'error', text: '密码长度至少8位' });
      return;
    }

    setLoading(true);
    try {
      await authAPI.confirmPasswordReset(
        formData.email,
        formData.code,
        formData.newPassword,
        formData.confirmPassword
      );
      setMessage({ type: 'success', text: '密码重置成功！' });
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      const errorMsg = err.response?.data?.code?.[0] ||
        err.response?.data?.new_password?.[0] ||
        err.response?.data?.detail ||
        '密码重置失败，请稍后重试';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-background">
        <div className="auth-bg-shape auth-bg-shape-1"></div>
        <div className="auth-bg-shape auth-bg-shape-2"></div>
        <div className="auth-bg-shape auth-bg-shape-3"></div>
      </div>

      <div className="auth-container">
        <div className="auth-card animate-slideUp">
          <div className="auth-header">
            <div className="auth-logo">
              <svg viewBox="0 0 40 40" fill="none">
                <rect width="40" height="40" rx="10" fill="url(#logo-gradient)" />
                <path
                  d="M12 20h16M20 12v16"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
                <defs>
                  <linearGradient id="logo-gradient" x1="0" y1="0" x2="40" y2="40">
                    <stop stopColor="#0d9488" />
                    <stop offset="1" stopColor="#0f766e" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <h1 className="auth-title">忘记密码</h1>
            <p className="auth-subtitle">
              {step === 1
                ? '输入您的邮箱地址，我们将发送验证码'
                : '输入验证码和新密码'}
            </p>
          </div>

          {/* Step indicators */}
          <div className="auth-steps">
            <div className={`auth-step ${step >= 1 ? 'active' : ''}`}>
              <span className="auth-step-number">1</span>
              <span className="auth-step-label">验证邮箱</span>
            </div>
            <div className="auth-step-line"></div>
            <div className={`auth-step ${step >= 2 ? 'active' : ''}`}>
              <span className="auth-step-number">2</span>
              <span className="auth-step-label">重置密码</span>
            </div>
          </div>

          {message.text && (
            <Alert
              type={message.type}
              message={message.text}
              onClose={() => setMessage({ type: '', text: '' })}
            />
          )}

          {step === 1 ? (
            <form className="auth-form" onSubmit={handleRequestCode}>
              <Input
                name="email"
                type="email"
                label="邮箱地址"
                placeholder="请输入您注册时的邮箱"
                value={formData.email}
                onChange={handleChange}
                icon={
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                }
              />

              <Button type="submit" fullWidth loading={loading}>
                发送验证码
              </Button>
            </form>
          ) : (
            <form className="auth-form" onSubmit={handleResetPassword}>
              <Input
                name="code"
                label="验证码"
                placeholder="请输入6位验证码"
                value={formData.code}
                onChange={handleChange}
                icon={
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                }
              />

              <Input
                name="newPassword"
                type="password"
                label="新密码"
                placeholder="请输入新密码（至少8位）"
                value={formData.newPassword}
                onChange={handleChange}
                icon={
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                }
              />

              <Input
                name="confirmPassword"
                type="password"
                label="确认密码"
                placeholder="请再次输入新密码"
                value={formData.confirmPassword}
                onChange={handleChange}
                icon={
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                }
              />

              <div className="auth-form-actions">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setStep(1)}
                >
                  返回
                </Button>
                <Button type="submit" loading={loading}>
                  重置密码
                </Button>
              </div>
            </form>
          )}

          <div className="auth-footer">
            <Link to="/login" className="auth-link">
              ← 返回登录
            </Link>
          </div>
        </div>

        <p className="auth-copyright">
          © 2024 账号管理系统. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;

